/**
 * What the interface shows when it has nothing to show, is still fetching, or
 * has failed.
 *
 * DESIGN_LANGUAGE.md §1 ("Fast"): *"Loading states are designed, not spinners
 * inserted where data was missing."* `Skeleton` is that — a shape the size of
 * the content that is coming, so the page does not reflow when it arrives.
 */

import type { ReactNode } from 'react';

import { describeFailure } from '~/api/problem';
import { cx } from '~/lib/cx';
import { Button } from './Button';
import { AlertOctagon, AlertTriangle, CheckCircle, Info, type IconProps } from './icons';
import type { Tone } from './Status';

import styles from './Feedback.module.css';

/* ====================================================================== *
 * Alert
 * ====================================================================== */

const ALERT_GLYPH: Partial<Record<Tone, (p: IconProps) => ReactNode>> = {
  success: CheckCircle,
  warning: AlertTriangle,
  danger: AlertOctagon,
  info: Info,
  neutral: Info,
};

export interface AlertProps {
  tone?: Extract<Tone, 'success' | 'warning' | 'danger' | 'info' | 'neutral'>;
  title?: ReactNode | undefined;
  children?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  className?: string | undefined;
  /**
   * Announce this alert when it appears.
   *
   * Off by default, because most alerts are on the page when it renders and a
   * live region that fires on mount makes a screen reader read the page twice.
   * On for anything that appears in response to something the user just did.
   *
   * `true` derives the urgency from the tone, which is the right default and
   * the wrong answer often enough to need an override. It was wrong on the
   * sign-in form: a refused password is `danger` and announced assertively,
   * a rate limit is `warning` and was announced politely — the same action,
   * the same outcome ("you are not signed in"), two different urgencies,
   * decided by what colour the box happened to be. Urgency is an
   * accessibility property and tone is a visual one; `'polite'` and
   * `'assertive'` say which is wanted without arguing about the colour.
   */
  live?: boolean | 'polite' | 'assertive';
}

export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  className,
  live = false,
}: AlertProps) {
  const Glyph = ALERT_GLYPH[tone] ?? Info;
  const urgency = live === true ? (tone === 'danger' ? 'assertive' : 'polite') : live;

  return (
    <div
      className={cx(styles.alert, styles[tone], className)}
      {...(urgency
        ? {
            role: urgency === 'assertive' ? 'alert' : 'status',
            'aria-live': urgency,
          }
        : {})}
    >
      <Glyph size={16} className={styles.alertGlyph} />
      <div className={styles.alertBody}>
        {title ? <p className={styles.alertTitle}>{title}</p> : null}
        {children ? <div className={styles.alertText}>{children}</div> : null}
      </div>
      {actions ? <div className={styles.alertActions}>{actions}</div> : null}
    </div>
  );
}

/* ====================================================================== *
 * Empty state
 * ====================================================================== */

export interface EmptyStateProps {
  /** What is not here. A statement, not an apology. */
  title: ReactNode;
  /**
   * Why, and what would change it.
   *
   * The most useful sentence in the product, written repeatedly and badly
   * elsewhere. An empty state that says "No repositories" and stops leaves the
   * user to work out whether that is because they have none, because they
   * cannot see them, or because something is broken.
   */
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Fills the available height rather than sitting at its natural size. */
  fill?: boolean;
  /**
   * How much room this is allowed to take.
   *
   * `page` is the centred, generous treatment, and is right when the empty
   * state *is* the page. `inline` is for an empty section inside a page that
   * has other content — a band on the Overview, the token list in Settings.
   *
   * The distinction exists because getting it wrong is very visible: three
   * page-sized empty states stacked on a fresh account turned the command
   * center into two thousand pixels of mostly nothing, and a four-row Settings
   * page spent four hundred pixels saying "No access tokens".
   */
  variant?: 'page' | 'inline';
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  fill = false,
  variant = 'page',
}: EmptyStateProps) {
  return (
    <div
      className={cx(
        styles.empty,
        variant === 'inline' ? styles.emptyInline : styles.emptyPage,
        fill && styles.emptyFill,
        className,
      )}
    >
      {icon ? <div className={styles.emptyIcon}>{icon}</div> : null}
      <div className={styles.emptyText}>
        <p className={styles.emptyTitle}>{title}</p>
        {description ? <p className={styles.emptyDescription}>{description}</p> : null}
      </div>
      {action ? <div className={styles.emptyAction}>{action}</div> : null}
    </div>
  );
}

/* ====================================================================== *
 * Error state
 * ====================================================================== */

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** What the application was doing: "load your repositories". */
  context?: string;
  className?: string;
}

/**
 * A failure, rendered so that a support conversation can start from it.
 *
 * The trace id is shown. The contract calls it *"the single most useful field
 * in a support conversation"* and it is: without it, a report is "it said
 * something went wrong at about four o'clock". It is rendered as selectable
 * monospace rather than hidden behind a "details" disclosure, because the
 * person who needs it is already looking at the error.
 */
export function ErrorState({ error, onRetry, context, className }: ErrorStateProps) {
  // Every decision about how a failure reads — the words, the tone, whether a
  // retry is even sensible — belongs to `describeFailure` so that the same
  // status cannot be presented two ways on two pages.
  const failure = describeFailure(error);

  return (
    <Alert
      tone={failure.tone}
      live
      className={cx(styles.errorState, className)}
      // `context` names what the application was doing, which the contract
      // cannot know. It is a prefix, not a replacement: "Could not load your
      // repositories" above "Too many attempts…" says both halves.
      title={context ? `Could not ${context}` : failure.title}
      actions={
        // A retry is offered only where retrying could produce a different
        // answer. A "Retry" under a 403 is a button that will fail again, and
        // offering it suggests the user did something wrong.
        onRetry && failure.retryable ? (
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null
      }
    >
      {context ? <p className={styles.failureTitle}>{failure.title}</p> : null}
      <p>{failure.message}</p>
      {failure.traceId ? (
        <p className={styles.traceId}>
          Trace <code>{failure.traceId}</code>
        </p>
      ) : null}
    </Alert>
  );
}

/* ====================================================================== *
 * Skeleton
 * ====================================================================== */

export interface SkeletonProps {
  /** CSS width. A number is pixels. */
  width?: string | number;
  height?: string | number;
  className?: string;
}

export function Skeleton({ width, height, className }: SkeletonProps) {
  return (
    <span
      className={cx(styles.skeleton, className)}
      style={{
        ...(width === undefined ? {} : { width: typeof width === 'number' ? `${width}px` : width }),
        ...(height === undefined
          ? {}
          : { height: typeof height === 'number' ? `${height}px` : height }),
      }}
      aria-hidden="true"
    />
  );
}

export interface SkeletonRowsProps {
  count?: number;
  /** An accessible name for what is loading: "Loading your repositories". */
  label: string;
  className?: string;
}

/**
 * Placeholder rows at the register's own row height.
 *
 * One live region for the whole group rather than one per row: a screen reader
 * should hear "loading your repositories" once, not eight times.
 */
export function SkeletonRows({ count = 4, label, className }: SkeletonRowsProps) {
  return (
    <div className={cx(styles.skeletonRows, className)} role="status" aria-live="polite">
      <span className={styles.visuallyHidden}>{label}</span>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={styles.skeletonRow}>
          <Skeleton width={`${String(42 + ((index * 17) % 38))}%`} height={12} />
          <Skeleton width={64} height={12} />
        </div>
      ))}
    </div>
  );
}
